import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useWebSocket } from '../../context/WebSocketContext';
import UploadPhotosModal from './UploadPhotosModal';
import GalleryGrid from './GalleryGrid';
import Lightbox from './LightBox';
import ManageBar from './ManageBar';
import AlbumsSideBar from './AlbumsSideBar';
import '../../styles/components/profile_page/PortfolioSection.css';

const PortfolioSection = ({
  photographerId,
  isOwner,
  authFetch,
  userSub,
  firstName
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [albums, setAlbums] = useState({});
  const [currentAlbumName, setCurrentAlbumName] = useState("");
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const handleOpenPortfolioModal = () => setPortfolioModalOpen(true);
  const handleClosePortfolioModal = () => setPortfolioModalOpen(false);

const { webSocketRef } = useWebSocket();

  const fetchPortfolioImages = useCallback(async () => {
    if (!photographerId) return;

    try {
      const response = await fetch(
        `https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev/users/${photographerId}/portfolio-images`
      );

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`Failed to fetch portfolio images (${response.status}) ${body}`);
      }

      const data = await response.json();
      const groups = data.reduce((acc, image) => {
          const albumName = image.tags?.album_name || "Uncategorized";
          if (!acc[albumName]) acc[albumName] = [];
          acc[albumName].push(image);
          return acc;
        }, {});

      setAlbums(groups);
      setCurrentAlbumName(prev =>
        prev && groups[prev] ? prev : Object.keys(groups)[0] || ""
      );
    } catch (e) {
      console.error("Error fetching portfolio images:", e);
    }
  }, [photographerId]);

  useEffect(() => {
    const handler = (event) => {
      const data = JSON.parse(event.data);

      if (data.action === "updatePortfolioPicture" && data.userSub === userSub) {
        fetchPortfolioImages();
      }
    };

    const ws = webSocketRef.current;

    if (ws) ws.addEventListener("message", handler);

    return () => ws?.removeEventListener("message", handler);
  }, [webSocketRef, userSub, fetchPortfolioImages]);

  const photos = useMemo(() => albums[currentAlbumName] || [], [albums, currentAlbumName]);

  const toggleSelect = (url) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedCount = selected.size;

  const createAlbumInline = () => {
    const name = window.prompt('New album name:');
    if (!name) return;
    // MVP: just select it so the user sees it exist (no backend yet)
    setCurrentAlbumName(name);
  };

  const albumPhotoCountText = `${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`;

  /* ----------------- Two-row clamp + accurate min-height ----------------- */
  const gridWrapRef = useRef(null); // wrapper around grid (for width/cols estimate)
  const gridRef = useRef(null);     // actual grid element (we’ll measure this)

  const [estimatedCols, setEstimatedCols] = useState(4); // for initial clamp count only
  const [isNarrow, setIsNarrow] = useState(false);       // <= 720px => 1 row, else 2 rows
  const [expanded, setExpanded] = useState(false);       // Show all tiles (true) or clamp (false)
  const [minGridHeight, setMinGridHeight] = useState(0); // px; ensures 2 rows visually

  const GAP_FALLBACK = 12;

  useEffect(() => {
    fetchPortfolioImages();
  }, [fetchPortfolioImages]);

  const handleUploadPhotos = async (filesOrFileList, albumNameInput) => {
    const filesArray = Array.from(filesOrFileList || []);
    if (!filesArray.length) throw new Error('No files selected');

    if (!userSub) throw new Error('No user sub available');

    const albumName = (albumNameInput || '').trim() || 'Uncategorized';
    const endpoint = `https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev/user-images/${userSub}/presigned`;

    const items = filesArray.map((file) => ({
      kind: 'album',
      group: albumName,
      contentType: file.type || 'application/octet-stream',
    }));

    const response = await authFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const t = await response.text().catch(() => '');
      throw new Error(`Failed to get presigned URLs (${response.status}) ${t}`);
    }

    const urlsData = await response.json();

    for (let i = 0; i < urlsData.length; i++) {
      const uploadRes = await fetch(urlsData[i].presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': urlsData[i].contentType,
          'x-amz-meta-kind': items[i].kind,
          'x-amz-meta-group': items[i].group,
        },
        body: filesArray[i],
      });

      if (!uploadRes.ok) {
        const t = await uploadRes.text().catch(() => '');
        throw new Error(`Failed to upload ${filesArray[i].name} (${uploadRes.status}) ${t}`);
      }
    }

    await fetchPortfolioImages();
  };

  const handleDeleteImage = async (imageUrl) => {
    try {
      const sub = userSub;
      const endpoint = `https://g3xew8hcjk.execute-api.eu-west-2.amazonaws.com/dev/users/${sub}/portfolio-images?imageUrl=${encodeURIComponent(imageUrl)}`;
      const response = await authFetch(endpoint, { method: 'DELETE' });

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`Failed to delete portfolio image (${response.status}) ${body}`);
      }

      await fetchPortfolioImages();
    } catch (err) {
      console.error('Error in handleDeleteImage:', err);
    }
  };

  // Estimate columns from wrapper width (only to decide how many to slice for the clamp)
  useEffect(() => {
    if (!gridWrapRef.current) return;
    const el = gridWrapRef.current;

    const compute = () => {
      const width = el.clientWidth || 0;
      const minTile = 180; // matches CSS minmax(180px, 1fr)
      const col = Math.max(1, Math.floor(width / minTile));
      setEstimatedCols(col);
    };

    const ro = new ResizeObserver(compute);
    ro.observe(el);
    compute();
    return () => ro.disconnect();
  }, []);

  // Media query: 1 row on small screens, 2 rows otherwise
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = (e) => setIsNarrow(e.matches);
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const rows = isNarrow ? 1 : 2;
  const initialCount = estimatedCols * rows;

  // When entering select mode, show all (no clamp)
  useEffect(() => {
    if (selecting) setExpanded(true);
  }, [selecting]);

  // When album changes, reset selection/clamp
  useEffect(() => {
    setExpanded(false);
    clearSelection();
    setSelecting(false);
  }, [currentAlbumName]);

  // Measure the **actual** grid (columns, tile height, gap) to compute the true min-height
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const items = Array.from(grid.children);
    if (!items.length) {
      setMinGridHeight(0);
      return;
    }

    // actual gap
    const cs = getComputedStyle(grid);
    const gap = parseFloat(cs.gap) || GAP_FALLBACK;

    // actual columns: count how many items share the first row's top offset
    const firstTop = items[0].offsetTop;
    const actualCols = items.filter((n) => n.offsetTop === firstTop).length || 1;

    // tile height (from first item)
    const tileH = items[0].getBoundingClientRect().height;

    const minH = rows * tileH + Math.max(0, rows - 1) * gap;

    // Only enforce a min height if we have fewer items than two rows would show
    const currentAlbumPhotos = albums[currentAlbumName]?.length || 0;
    const needMin = currentAlbumPhotos < actualCols * rows;

    setMinGridHeight(needMin ? Math.round(minH) : 0);
  }, [rows, albums, currentAlbumName, selecting, expanded]);

  const visiblePhotos = expanded ? photos : photos.slice(0, initialCount);
  const canToggleClamp = photos.length > initialCount && !selecting;

  /* ---------------------------------------------------------------------- */

  return (
    <section className="portfolio">
      {/* Small section title helps scannability */}
      <h2 className="portfolio__sectionTitle">
        {isOwner ? 'Your Portfolio' : 'Portfolio'}
      </h2>

      <div className="portfolio__layout">
        {(isOwner || Object.keys(albums).length > 0) && (
          <AlbumsSideBar
            albums={albums}
            current={currentAlbumName}
            onSelect={(name) => { clearSelection(); setCurrentAlbumName(name); }}
            isOwner={isOwner}
            showCreate={false}
            onCreate={createAlbumInline}
            onRename={(name) => {
              const newName = prompt('Rename album:', name);
              if (!newName || newName === name) return;
              alert(`Pretend renamed "${name}" to "${newName}"`);
            }}
            onDelete={(name) => {
              if (window.confirm(`Delete album "${name}"? (photos would move to Loose photos)`)) {
                alert('Pretend deleted album');
              }
            }}
            onDropOnAlbum={(name, photoIds) => {
              console.log('Would move', photoIds, 'to', name);
              clearSelection();
            }}
          />
        )}

        <div className="portfolio__main">
          {/* Header bar */}
          <div className="portfolio__header">
            <div className="portfolio__heading">
              <h3 className="portfolio__albumName">{currentAlbumName || 'Loose photos'}</h3>
              <span className="portfolio__photoCount">{albumPhotoCountText}</span>
            </div>

            {isOwner && (
              <div className="portfolio__actions">
                <button className="pf-btn pf-btn--primary" onClick={handleOpenPortfolioModal}>
                  Upload
                </button>
                <button className="pf-btn" onClick={createAlbumInline}>+ Album</button>
                <button
                  className="pf-btn"
                  onClick={() => {
                    setSelecting((s) => {
                      const next = !s;
                      if (!next) clearSelection();
                      return next;
                    });
                  }}
                >
                  {selecting ? 'Done' : 'Select'}
                </button>
              </div>
            )}
          </div>

          {/* Sticky bulk bar – only in select mode */}
          {isOwner && selecting && (
            <ManageBar
              selectedCount={selectedCount}
              albums={albums}
              currentAlbum={currentAlbumName}
              onMoveTo={(album) => {
                console.log('Would move', [...selected], 'to', album);
                clearSelection();
              }}
              onDelete={() => {
                if (!selectedCount) return;
                if (window.confirm(`Delete ${selectedCount} photo(s)?`)) {
                  [...selected].forEach((url) => handleDeleteImage(url));
                  clearSelection();
                }
              }}
              onSetCover={() => {
                if (selectedCount !== 1) return;
                const first = [...selected][0];
                alert(`Pretend set cover: ${first}`);
                clearSelection();
              }}
              // Pass both for compatibility with your ManageBar version
              onToggleSelecting={() => { setSelecting(false); clearSelection(); }}
              onDone={() => { setSelecting(false); clearSelection(); }}
            />
          )}

          {/* Grid */}
          <div className="portfolio__gridWrap" ref={gridWrapRef}>
            {/* min-height box ensures we always reserve 2 rows on desktop */}
            <div style={{ minHeight: minGridHeight }}>
              <GalleryGrid
                photos={visiblePhotos}
                isOwner={isOwner}
                selecting={selecting}
                selected={selected}
                onToggleSelect={toggleSelect}
                onDelete={(url) => {
                  if (window.confirm('Delete this image?')) handleDeleteImage(url);
                }}
                onClickPhoto={(index) => setLightboxIndex(index)}
                onSetCover={(url) => alert(`Pretend set cover: ${url}`)}
                onToggleVisibility={(url) => alert(`Pretend toggle visibility: ${url}`)}
                gridRef={gridRef}   // NEW: let us measure the real grid
              />
            </div>

            {!photos.length && (
              <div className="portfolio__empty">
                {isOwner ? (
                  <>
                    <div className="portfolio__emptyTitle">No photos here yet</div>
                    <p>Upload your first photos to start building your portfolio.</p>
                  </>
                ) : (
                  <div className="portfolio__emptyTitle">
                    {firstName || 'This photographer'} hasn't uploaded any photos yet.
                  </div>
                )}
              </div>
            )}

            {canToggleClamp && !expanded && (
              <div style={{ marginTop: 10 }}>
                <button className="pf-btn pf-btn--secondary" onClick={() => setExpanded(true)}>
                  Show more
                </button>
              </div>
            )}
            {canToggleClamp && expanded && !selecting && (
              <div style={{ marginTop: 10 }}>
                <button className="pf-btn" onClick={() => setExpanded(false)}>
                  Show less
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <UploadPhotosModal
        isOpen={portfolioModalOpen}
        onClose={handleClosePortfolioModal}
        onUpload={handleUploadPhotos}
      />

      {lightboxIndex >= 0 && photos.length > 0 && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
          onPrev={() => setLightboxIndex((i) => (i - 1 + photos.length) % photos.length)}
          onNext={() => setLightboxIndex((i) => (i + 1) % photos.length)}
        />
      )}
    </section>
  );
};

export default PortfolioSection;
